import { useCallback, useRef } from 'react';
import { useFocusEffect } from '@react-navigation/native';

/**
 * FE-255 PR5 — refetch screen data each time the screen gains focus.
 *
 * Pattern: every list/detail screen used to dispatch its load-data
 * thunk(s) once in `useEffect(..., [])` on mount. That's correct on
 * first navigation, but means data is stale forever once the screen
 * re-enters the foreground from a back-stack push, app-switch, or
 * tab-bar return. The optimistic-UI work in PRs 1–4 hides the lag
 * for mutations the user dispatched on THIS device — but cross-device
 * mutations (group member edits an item, someone hands off to me,
 * a borrow request flips status) only land via refetch.
 *
 * This hook wraps `useFocusEffect` from `@react-navigation/native`
 * with two pragmatic guards:
 *
 *   1. **Debounce by `minIntervalMs`** (default 30s). Without this,
 *      every tab-bar tap fires a fresh round-trip; users who flip
 *      between two tabs rapidly would hammer the BE. 30s is short
 *      enough that "I came back from another app" still refreshes,
 *      long enough that "I tapped Home then Browse then Home" is
 *      free. Override per-callsite if a screen has more aggressive
 *      staleness needs (notifications: 5s, dashboard: 60s).
 *
 *   2. **Skip first focus on mount** by default. The screen's
 *      existing mount-time `useEffect` already does the initial
 *      load; if we also fire on first focus, we'd double-fetch on
 *      every screen navigation. Opt in with `runOnMount: true` if
 *      you remove the mount-time load and want this hook to own it.
 *
 * The callback receives a fresh AbortController each call; if the
 * screen blurs/unmounts mid-flight, the controller aborts. Callers
 * forward the signal to fetch/axios via `dispatch(thunk({ signal }))`.
 *
 * Usage:
 *   useRefetchOnFocus(
 *     useCallback(
 *       (signal) => dispatch(loadInventory({ signal })),
 *       [dispatch],
 *     ),
 *   );
 *
 *   // Aggressive freshness:
 *   useRefetchOnFocus(refetch, { minIntervalMs: 5_000 });
 *
 *   // No separate mount-time load:
 *   useRefetchOnFocus(refetch, { runOnMount: true });
 *
 * The callback's identity matters. Wrap it in `useCallback` so this
 * hook doesn't re-subscribe every render — the deps array on
 * `useFocusEffect` is intentionally `[callback]` for that reason.
 */
export interface UseRefetchOnFocusOptions {
  /**
   * Minimum gap between focus-triggered refetches, in ms. A focus
   * that lands inside this window is dropped (we still mark the
   * focus event but don't dispatch). Defaults to 30_000.
   */
  minIntervalMs?: number;
  /**
   * When true, fire the callback on the FIRST focus event (i.e.
   * the mount focus). Default false — the assumption is the screen
   * already loads on mount via a separate `useEffect`. Flip to
   * true if you've removed the mount-time load and want this hook
   * to own both initial-and-subsequent loads.
   */
  runOnMount?: boolean;
}

type RefetchCallback = (signal: AbortSignal) => unknown;

const DEFAULT_MIN_INTERVAL_MS = 30_000;

export function useRefetchOnFocus(
  callback: RefetchCallback,
  options: UseRefetchOnFocusOptions = {},
): void {
  const { minIntervalMs = DEFAULT_MIN_INTERVAL_MS, runOnMount = false } =
    options;

  // Per-mount state — survives re-renders but resets when the screen
  // unmounts and remounts (which is what we want — a fresh navigation
  // means a fresh debounce window).
  const lastRunAtRef = useRef<number>(0);
  const isFirstFocusRef = useRef<boolean>(true);

  useFocusEffect(
    useCallback(() => {
      const isFirst = isFirstFocusRef.current;
      isFirstFocusRef.current = false;

      // Skip the mount-focus when caller opts out.
      if (isFirst && !runOnMount) {
        // Still mark the time so the FIRST subsequent focus respects
        // the debounce — otherwise a quick blur/refocus right after
        // mount would fire twice within the window.
        lastRunAtRef.current = Date.now();
        return;
      }

      const now = Date.now();
      if (now - lastRunAtRef.current < minIntervalMs) {
        // Inside debounce — drop this focus event but don't reset
        // the timer (otherwise rapid focus events would push it
        // forward forever and we'd never refetch).
        return;
      }

      lastRunAtRef.current = now;
      const controller = new AbortController();
      try {
        callback(controller.signal);
      } catch {
        // Synchronous throws from the callback should not crash the
        // navigation focus handler. Async errors are the caller's
        // problem (they're on the dispatched thunk's rejected path).
      }

      // Cleanup runs on blur/unmount — abort any in-flight request
      // so we don't double-apply once the screen comes back.
      return () => {
        controller.abort();
      };
    }, [callback, minIntervalMs, runOnMount]),
  );
}
